import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { loginPositionAdmin } from '@/api/auth.api';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { logError } from '@/utils/logger.utils';

export const AdminLoginPage = () => {
	const [password, setPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const { positionId } = useParams<{ positionId: string }>();
	const { positionStatus, setPositionAdminAuthToken } = useAuth();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!positionId) return;

		setIsLoading(true);

		try {
			const response = await loginPositionAdmin(password, positionId);

			// Handle different response codes
			switch (response.httpResponseCode) {
				case 200:
					// Success - store token and navigate
					if (response.data?.positionAdminAuthToken) {
						setPositionAdminAuthToken(response.data.positionAdminAuthToken);
						toast.success('Successfully logged in as admin');
						navigate(`/${positionId}/login/candidate`);
					} else {
						throw new Error('no token received while logging in as admin');
					}
					break;
				case 400:
				case 401:
				case 404:
				case 500:
					toast.error(response.message);
					break;
				default:
					throw new Error('invalid response code while logging in as admin');
			}
		} catch (error) {
			logError(error);
			toast.error(
				'We are facing some issues while logging you in. Please refresh the page and try again. If you think this is an error, please contact support.'
			);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-blue-50 flex items-center justify-center px-4">
			<Card className="w-full max-w-md shadow-lg">
				<CardHeader className="text-center">
					<div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
						<Shield className="w-6 h-6 text-blue-600" />
					</div>
					<CardTitle className="text-2xl font-semibold">Admin Login</CardTitle>
					<CardDescription>
						{positionStatus?.title ? (
							<>
								Enter your admin credentials for position{' '}
								<strong>{positionStatus.title}</strong>
							</>
						) : (
							'Enter your admin credentials to access this position'
						)}
					</CardDescription>
				</CardHeader>

				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="password">Admin Password</Label>
							<Input
								id="password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Enter admin password"
								required
							/>
						</div>

						<Button type="submit" className="w-full" disabled={isLoading}>
							{isLoading ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Logging in...
								</>
							) : (
								'Login as Admin'
							)}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
};
